import { Component, Host, h, Prop, Element } from '@stencil/core';
import { addIntersectionObserver } from '../../utils/utils';


@Component({
  tag: 'success-story-block',
  styleUrl: 'success-story-block.css',
  shadow: true,
})
export class SuccessStoryBlock {
  /**
   * The Content Type
   */
  @Prop() contentType: string;

  /**
   * Story Data
   */
  @Prop() storyData: string;

  /**
   * Background Color
   */
  @Prop() backgroundColor: string;

  /**
   * Text Color
   */
  @Prop() textColor: string;

  /**
   * Padding Top
   */
  @Prop() paddingTop: string;

  /**
   * Padding Bottom
   */
  @Prop() paddingBottom: string;

  /**
   * Background Image Url
   */
  @Prop() backgroundImage: string;

  @Element() el: HTMLElement;

  private slideChangeInterval: any;
  private slideIndex: number = 0;

  constructor() {
    this.handleResponsiveTrackers = this.handleResponsiveTrackers.bind(this);
  }

  componentDidLoad() {
    this.handleResponsiveTrackers();
    window.addEventListener('resize', this.handleResponsiveTrackers);
    this.startCarousel();
    addIntersectionObserver(
      this.el.shadowRoot.querySelector('.story-block-slide'),
      () => {
        this.el.shadowRoot.querySelector('.story-block-slide').classList.add('animate');
      },
      ['.overlay1', '.overlay2'],
    );
  }

  disconnectedCallback() {
    this.stopCarousel();
  }

  startCarousel() {
    const slideToggles = this.el.shadowRoot.querySelectorAll('.story-block-bottom-tracker');
    const totalSlides = slideToggles.length;
    if (totalSlides > 1) {
      this.slideChangeInterval = setInterval(() => {
        this.nextSlide();
      }, 4500); // Carousel Cycle Interval
    }
  }

  stopCarousel() {
    clearInterval(this.slideChangeInterval);
  }

  nextSlide() {
    const assetsContainer = this.el.shadowRoot.querySelector('.story-block-assets');
    const slideContent = this.el.shadowRoot.querySelector('.story-block-right');
    const slideToggles = this.el.shadowRoot.querySelectorAll('.story-block-bottom-tracker');
    const totalSlides = slideToggles.length;

    // Remove the active class from the current slide
    slideToggles[this.slideIndex].classList.remove('active');

    // Calculate the next slide index
    this.slideIndex = (this.slideIndex + 1) % totalSlides;
    const activeSlide = slideToggles[this.slideIndex];

    // Add the active class to the next slide
    slideToggles[this.slideIndex].classList.add('active');
    this.animateSequence(assetsContainer, activeSlide, slideContent);
  }

  setActiveSlide(activeIndex) {
    const slideToggles = this.el.shadowRoot.querySelectorAll('.story-block-bottom-tracker');
    slideToggles.forEach((slide, index) => {
      if (index === activeIndex) {
        slide.classList.add('active');
      } else {
        slide.classList.remove('active');
      }
    });
  }

  updateSlideContent(activeSlide, assetsContainer, slideContent) {
    // Extract data attributes from the active slide
    const description = activeSlide.getAttribute('data-description');
    const name = activeSlide.getAttribute('data-name');
    const position = activeSlide.getAttribute('data-position');
    const assetType = activeSlide.getAttribute('data-type');
    const video = activeSlide.getAttribute('data-video');
    const imageUrl = activeSlide.getAttribute('data-image');
    const imageAlt = activeSlide.getAttribute('data-alt');
    const logoUrl = activeSlide.getAttribute('data-logo');
    const logoAlt = activeSlide.getAttribute('data-logalt');
    const buttonsJson = activeSlide.getAttribute('data-buttons');
    const buttons = buttonsJson ? JSON.parse(buttonsJson) : [];

    // Select elements within the component to update their content
    if (slideContent) {
      slideContent.querySelector('.story-block-right-description').innerHTML = description || '';
      slideContent.querySelector('.story-block-right-name').textContent = name || '';
      slideContent.querySelector('.story-block-right-position').textContent = position || '';

      if (video && assetType === 'video') {
        // If there's a video URL, add the 'video-active' class to show the video and hide the image
        assetsContainer.classList.add('video-active');

        // Assuming you have a <video-block> element to update
        const videoBlock = this.el.shadowRoot.querySelector('.story-block-assets video-block') as any;
        if (videoBlock) {
          videoBlock.videoUrl = video; // Update the video URL
        }
      } else {
        // If there's no video URL, remove the 'video-active' class to show the image and hide the video
        assetsContainer.classList.remove('video-active');
      }

      // For the image, you might need to handle it differently
      const imageBlock = this.el.shadowRoot.querySelector('.story-block-assets .story-block-image-wrapper image-block') as any;
      if (imageBlock) {
        // Directly set properties if they're defined on the image-block component
        imageBlock.imageUrl = imageUrl || '';
        imageBlock.imageAlt = imageAlt || '';
        // Assume imageClass is a property; if not, use setAttribute for actual attributes
        imageBlock.imageClass = 'story-block-image';
      }

      // For the image, you might need to handle it differently
      const logoBlock = this.el.shadowRoot.querySelector('.story-block-assets .story-block-logo-wrapper image-block') as any;
      const logoBlockWrapper = this.el.shadowRoot.querySelector('.story-block-assets .story-block-logo-wrapper') as any;
      if (logoBlock) {
        // Directly set properties if they're defined on the image-block component
        logoBlock.imageUrl = logoUrl || '';
        logoBlock.imageAlt = logoAlt || '';
        // Assume imageClass is a property; if not, use setAttribute for actual attributes
        logoBlock.imageClass = 'story-block-logo';

        if(!logoUrl) {
          logoBlockWrapper.style.display = 'none';
        }
        else {
          logoBlockWrapper.style.display = 'flex';
        }
      }
      else {
        logoBlockWrapper.style.display = 'none';
      }

      // Correctly selecting the buttons container based on the actual class name used in your render method
      const buttonsContainer = this.el.shadowRoot.querySelector('.story-block-right-buttons');
      const existingButtonBlocks = buttonsContainer ? buttonsContainer.querySelectorAll('button-block') : [];

      // Dynamically updating, creating, or removing button-block elements based on the current slide's button data
      buttons.forEach((buttonData, index) => {
        if (index < existingButtonBlocks.length) {
          existingButtonBlocks[index].buttonsData = buttonData;
        } else {
          const newButtonBlock = document.createElement('button-block');
          newButtonBlock.buttonsData = buttonData;
          buttonsContainer.appendChild(newButtonBlock);
        }
      });

      // Remove excess button-blocks if there are more existing than needed
      if (existingButtonBlocks.length > buttons.length) {
        for (let i = buttons.length; i < existingButtonBlocks.length; i++) {
          //console.log(buttons.length);
          buttonsContainer.removeChild(existingButtonBlocks[i]);
        }
      }

      // If there are no buttons for the current slide
      if (buttons.length === 0) {
        if (buttonsContainer) {
          buttonsContainer.innerHTML = '';
        }
      }
    }
  }

  displaySelectedSlide(selectedIndex) {
    // Stop the carousel
    this.stopCarousel();

    // Update active class for slides
    const slideToggles = this.el.shadowRoot.querySelectorAll('.story-block-bottom-tracker');
    slideToggles.forEach((slide, idx) => {
      if (idx === selectedIndex) {
        slide.classList.add('active');
      } else {
        slide.classList.remove('active');
      }
    });

    // Extract data attributes from the selected slide
    const activeSlide = slideToggles[selectedIndex];

    const assetsContainer = this.el.shadowRoot.querySelector('.story-block-assets');

    // Select elements within the component to update their content
    const slideContent = this.el.shadowRoot.querySelector('.story-block-right');
    if (slideContent) {
      // Reset or initiate the animation sequence
      this.animateSequence(assetsContainer, activeSlide, slideContent);
    }
  }

  animateSequence(assetsContainer, activeSlide, slideContent) {
    // Start by triggering fade-out animation
    assetsContainer.classList.add('animate-out');
    slideContent.classList.add('animate-out');
    // Listen for the end of the fade-out animation
    assetsContainer.onanimationend = event => {
      if (event.animationName === 'slide-fade-out') {
        assetsContainer.classList.remove('animate-out');
        // this.updateContent(activeSlide, assetsContainer, slideContent);
        this.updateSlideContent(activeSlide, assetsContainer, slideContent);
        assetsContainer.classList.add('animate-in');
      } else if (event.animationName === 'slide-fade-in') {
        assetsContainer.classList.remove('animate-in');
        assetsContainer.classList.add('active');
      }
    };

    slideContent.onanimationend = event => {
      if (event.animationName === 'slide-fade-out') {
        slideContent.classList.remove('animate-out');
        // this.updateSlideContent(activeSlide, assetsContainer, slideContent);
        slideContent.classList.add('animate-in');
      } else if (event.animationName === 'slide-fade-in') {
        slideContent.classList.remove('animate-in');
        slideContent.classList.add('active');
      }
    };
  }

  handleResponsiveTrackers() {
    const desktopContainer = this.el.shadowRoot.querySelector('#desktopTrackers') as any;
    const mobileContainer = this.el.shadowRoot.querySelector('#mobileTrackers') as any;
    const trackers = this.el.shadowRoot.querySelector('.story-block-bottom-trackers') as any;
  
    if (trackers && window.innerWidth > 1000) {
      if (desktopContainer && trackers.parentNode !== desktopContainer) {
        desktopContainer.appendChild(trackers);
      }
    } else {
      if (mobileContainer && trackers.parentNode !== mobileContainer) {
        mobileContainer.appendChild(trackers);
      }
    }
  }

  render() {
    const stories = JSON.parse(this.storyData);
    return (
      <Host>
        <div class={`container ${this.paddingBottom} ${this.paddingTop}`}>
          <div
            class={`story-block-container ${this.textColor} ${this.backgroundColor} ${this.contentType}`}
            style={{ backgroundImage: `url(${this.backgroundImage})`, backgroundRepeat: 'no-repeat', backgroundSize: 'cover' }}
          >
            <div class={`story-block-slide`}>
              <div class={`story-block-left relative`}>
                <div class="overlay1"></div>
                {stories.length > 0 && (
                  <div class={`story-block-assets ${stories[0].assetType === 'video' && stories[0].video ? 'video-active' : ''}`}>
                    <div class={`story-block-image-wrapper aspect-square`}>
                      {/* {stories[0].image && <image-block imageClass={`story-block-image`} imageUrl={stories[0].image} imageAlt={stories[0].image_alt}></image-block>} */}
                      <image-block
                        imageClass={`story-block-image`}
                        imageUrl={stories.length > 0 ? stories[0].image || '' : ''}
                        imageAlt={stories.length > 0 ? stories[0].image_alt || '' : ''}
                      ></image-block>

                      <video-block videoUrl={stories.length > 0 ? stories[0].video || '' : ''} videoClass={`story-block-video`}></video-block>
                    </div>
                    <div class="story-block-logo-wrapper" style={{ display: stories[0].logo ? 'flex' : 'none' }}>
                      {/* {stories[0].logo && <image-block imageUrl={stories[0].logo} imageAlt={stories[0].logo_alt} imageClass="story-block-logo"></image-block>} */}
                      <image-block
                        imageUrl={stories[0].logo ? stories[0].logo : ''}
                        imageAlt={stories[0].logo ? stories[0].logo_alt : ''}
                        imageClass={`story-block-logo`}
                      ></image-block>
                    </div>
                  </div>
                )}
                <div id="desktopTrackers"></div>
              </div>
              {stories.length > 0 && (
                <div class={`story-block-right relative`}>
                  <div class={`overlay2`}></div>
                  {this.contentType === 'quote' && <p class={`story-block-quotes`}>“</p>}
                  <div class={`story-block-right-description`} innerHTML={stories[0].description}></div>
                  <p class={`story-block-right-name`}>{stories[0].name}</p>
                  <p class={`story-block-right-position`}>{stories[0].position}</p>
                  {/* {stories[0].button && stories[0].button.length > 0 && ( */}
                  <div class={`story-block-right-buttons `}>
                    {stories[0].button.map(button => (
                      <button-block buttonsData={button}></button-block>
                    ))}
                  </div>
                  {/* )} */}
                </div>
              )}
            </div>
            <div id="mobileTrackers"></div>
            <div class={`story-block-bottom-trackers`}>
              {stories.length > 0 &&
                stories.map(({ company, description, name, position, button, image, image_alt, video, logo, logo_alt, asset_type }, index) => (
                  <div
                    class={`story-block-bottom-tracker w-fit h-auto flex flex-col items-center justify-center text-left ${index === 0 ? 'active' : ''}`}
                    data-description={description}
                    data-name={name}
                    data-position={position}
                    data-buttons={JSON.stringify(button)}
                    data-image={image}
                    data-alt={image_alt}
                    data-logo={logo}
                    data-logalt={logo_alt}
                    data-video={video}
                    data-type={asset_type}
                    onClick={() => this.displaySelectedSlide(index)}
                  >
                    <div class="story-block-slide-preview-item-bar-wrapper">
                      <div class={`story-block-slide-preview-item-bar bar-${index}`}></div>
                    </div>
                    <h3 class={`font-poppins`}>{company}</h3>
                  </div>
                ))}
            </div>
          </div>
        </div>
        <link rel="stylesheet" id="plyr-css-css" href="https://cdn.plyr.io/3.7.8/plyr.css?ver=6.4.3" media="all" />
      </Host>
    );
  }
}
